export const FILTER_TYPE = {
  CONVERSATION: 0,
  CONTACT: 1,
};

export const CONVERSATION_LIST_ROUTE_NAMES = [
  'home',
  'inbox_dashboard',
  'label_conversations',
  'team_conversations',
  'folder_conversations',
  'conversation_mentions',
  'conversation_unattended',
  'conversation_participating',
];

const CONVERSATION_THROUGH_ROUTE_ALIASES = {
  conversations_through_label: 'label_conversations',
  conversation_through_inbox: 'inbox_dashboard',
  conversations_through_folders: 'folder_conversations',
  conversations_through_team: 'team_conversations',
  conversation_through_mentions: 'conversation_mentions',
  conversation_through_unattended: 'conversation_unattended',
  conversation_through_participating: 'conversation_participating',
};

export function normalizeConversationRoute(route) {
  if (!route?.name) return null;

  const normalizedName =
    CONVERSATION_THROUGH_ROUTE_ALIASES[route.name] || route.name;

  if (!CONVERSATION_LIST_ROUTE_NAMES.includes(normalizedName)) {
    return null;
  }

  return { ...route, name: normalizedName };
}

export function labelExists(labels, labelTitle) {
  if (!labelTitle) return false;
  const normalized = String(labelTitle);
  return (labels || []).some(label => String(label.title) === normalized);
}

export function isContactCustomView(view) {
  return (
    view?.filter_type === 'contact' ||
    view?.filter_type === FILTER_TYPE.CONTACT
  );
}

export function isConversationCustomView(view) {
  return (
    view?.filter_type === 'conversation' ||
    view?.filter_type === FILTER_TYPE.CONVERSATION
  );
}

export function isConversationListRoute(route) {
  return Boolean(normalizeConversationRoute(route));
}

export function getRouteToSaveLastConversationView(to, from) {
  const normalizedTo = normalizeConversationRoute(to);
  if (normalizedTo) return normalizedTo;

  const normalizedFrom = from && normalizeConversationRoute(from);
  if (normalizedFrom) return normalizedFrom;

  return null;
}

/** True when navigating within the same inbox/label/team/folder view (e.g. list → conversation). */
export function isSameConversationViewNavigation(to, from) {
  if (!to?.name || !from?.name) return false;

  const normalizedTo = normalizeConversationRoute(to);
  const normalizedFrom = normalizeConversationRoute(from);
  if (!normalizedTo || !normalizedFrom) return false;

  if (
    String(normalizedTo.params?.accountId ?? '') !==
    String(normalizedFrom.params?.accountId ?? '')
  ) {
    return false;
  }

  if (normalizedTo.name !== normalizedFrom.name) return false;

  switch (normalizedTo.name) {
    case 'folder_conversations':
      return (
        String(normalizedTo.params.id) === String(normalizedFrom.params.id)
      );
    case 'inbox_dashboard':
      return (
        String(normalizedTo.params.inbox_id) ===
        String(normalizedFrom.params.inbox_id)
      );
    case 'label_conversations':
      return (
        String(normalizedTo.params.label ?? '') ===
        String(normalizedFrom.params.label ?? '')
      );
    case 'team_conversations':
      return (
        String(normalizedTo.params.teamId) ===
        String(normalizedFrom.params.teamId)
      );
    case 'home':
    case 'conversation_mentions':
    case 'conversation_unattended':
    case 'conversation_participating':
      return true;
    default:
      return false;
  }
}

export function getConversationViewDataFromRoute(route) {
  const normalizedRoute = normalizeConversationRoute(route);
  if (!normalizedRoute) return null;

  let viewData = { type: 'home' };

  switch (normalizedRoute.name) {
    case 'folder_conversations':
      if (normalizedRoute.params.id && Number(normalizedRoute.params.id) !== 0) {
        viewData = { type: 'folder', id: String(normalizedRoute.params.id) };
      }
      break;
    case 'inbox_dashboard':
      if (
        normalizedRoute.params.inbox_id &&
        Number(normalizedRoute.params.inbox_id) !== 0
      ) {
        viewData = { type: 'inbox', id: String(normalizedRoute.params.inbox_id) };
      }
      break;
    case 'label_conversations':
      if (normalizedRoute.params.label) {
        viewData = { type: 'label', id: normalizedRoute.params.label };
      }
      break;
    case 'team_conversations':
      if (normalizedRoute.params.teamId) {
        viewData = { type: 'team', id: normalizedRoute.params.teamId };
      }
      break;
    case 'conversation_mentions':
      viewData = { type: 'mention' };
      break;
    case 'conversation_unattended':
      viewData = { type: 'unattended' };
      break;
    case 'conversation_participating':
      viewData = { type: 'participating' };
      break;
    default:
      break;
  }

  return viewData;
}

export const OVERVIEW_LEADS_TABS = {
  TASKS: 'tasks',
  PRIORITY_LEADS: 'priority_leads',
};

export function resolveLastOverviewLeadsTab(savedTab) {
  if (savedTab === OVERVIEW_LEADS_TABS.PRIORITY_LEADS) {
    return { valid: true, tab: OVERVIEW_LEADS_TABS.PRIORITY_LEADS };
  }

  return { valid: true, tab: OVERVIEW_LEADS_TABS.TASKS };
}

export function isExplicitAllViewQuery(route) {
  return route?.query?.view === 'all';
}

export function isSameAccountPreference(
  currentUser,
  accountId,
  preferenceKey,
  preferenceValue
) {
  const existing =
    currentUser?.ui_settings?.user_preferences_by_account?.[accountId]?.[
      preferenceKey
    ];
  return JSON.stringify(existing) === JSON.stringify(preferenceValue);
}

export function contactSegmentExists(segments, segmentId) {
  if (segmentId == null || segmentId === '' || String(segmentId) === '0') {
    return false;
  }
  const sid = Number(segmentId);
  return (segments || []).some(
    view => Number(view.id) === sid && isContactCustomView(view)
  );
}

export function conversationFolderExists(folders, folderId) {
  if (!folderId || Number(folderId) === 0) return false;
  const fid = Number(folderId);
  return (folders || []).some(
    view => Number(view.id) === fid && isConversationCustomView(view)
  );
}

export function inboxExists(inboxes, inboxId) {
  if (!inboxId || Number(inboxId) === 0) return false;
  return (inboxes || []).some(
    inbox => Number(inbox.id) === Number(inboxId)
  );
}

export function teamExists(teams, teamId) {
  if (!teamId) return false;
  return (teams || []).some(team => Number(team.id) === Number(teamId));
}

export function resolveLastContactView(lastContactView, { segments, labels }) {
  if (!lastContactView?.type || lastContactView.type === 'all') {
    return { valid: true, view: lastContactView || { type: 'all' } };
  }

  if (lastContactView.type === 'segment') {
    return contactSegmentExists(segments, lastContactView.id)
      ? { valid: true, view: lastContactView }
      : { valid: false, view: { type: 'all' } };
  }

  if (lastContactView.type === 'label') {
    return labelExists(labels, lastContactView.id)
      ? { valid: true, view: lastContactView }
      : { valid: false, view: { type: 'all' } };
  }

  return { valid: false, view: { type: 'all' } };
}

export function resolveLastConversationView(
  lastConversationView,
  { folders, labels, inboxes, teams }
) {
  if (!lastConversationView?.type || lastConversationView.type === 'home') {
    return { valid: true, view: lastConversationView || { type: 'home' } };
  }

  switch (lastConversationView.type) {
    case 'folder':
      return conversationFolderExists(folders, lastConversationView.id)
        ? { valid: true, view: lastConversationView }
        : { valid: false, view: { type: 'home' } };
    case 'inbox':
      return inboxExists(inboxes, lastConversationView.id)
        ? { valid: true, view: lastConversationView }
        : { valid: false, view: { type: 'home' } };
    case 'label':
      return labelExists(labels, lastConversationView.id)
        ? { valid: true, view: lastConversationView }
        : { valid: false, view: { type: 'home' } };
    case 'team':
      return teamExists(teams, lastConversationView.id)
        ? { valid: true, view: lastConversationView }
        : { valid: false, view: { type: 'home' } };
    case 'mention':
    case 'unattended':
    case 'participating':
      return { valid: true, view: lastConversationView };
    default:
      return { valid: false, view: { type: 'home' } };
  }
}

export function buildUpdatedAccountPreferences(
  accountId,
  currentUser,
  preferenceKey,
  preferenceValue
) {
  const user = currentUser || {};
  const existingUiSettings = user.ui_settings || {};
  const existingAccountPrefs =
    existingUiSettings.user_preferences_by_account || {};
  const currentAccountPrefs = existingAccountPrefs[accountId] || {};

  return {
    uiSettings: {
      ...existingUiSettings,
      user_preferences_by_account: {
        ...existingAccountPrefs,
        [accountId]: {
          ...currentAccountPrefs,
          [preferenceKey]: preferenceValue,
        },
      },
    },
  };
}

export function dispatchAccountPreference(
  store,
  accountId,
  preferenceKey,
  preferenceValue
) {
  const currentUser = store.getters.getCurrentUser;
  if (!currentUser?.id) {
    return Promise.resolve(false);
  }

  return store.dispatch(
    'updateUISettings',
    buildUpdatedAccountPreferences(
      accountId,
      currentUser,
      preferenceKey,
      preferenceValue
    )
  );
}
